import { useState } from 'react';

export const useLocalStorage = <T>(
  key: string,
  initialValue?: T,
): {
  storedValue: T;
  setValue: (value: T | ((value: T) => void)) => void;
  remove: (clearState?: boolean) => void;
} => {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once

  const [storedValue, setStoredValue] = useState(() => {
    try {
      if (typeof window === 'undefined') return initialValue;

      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error('useLocalStorage error', error);
      // If error also return initialValue
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: T | ((value: T) => void)) => {
    try {
      if (typeof window === 'undefined') return;

      // Allow value to be a function so we have the same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
        console.error('useLocalStorage.setValue error', error);
    }
  };

  // Remove the local storage item
  // Optionally turn off clearing state of the removed item in order to keep its value until page refresh or manual setting
  const remove = (clearState = true) => {
    try {
      if (typeof window === 'undefined') return;

      // Clear state
      if (clearState) setStoredValue(null);
      // Save to local storage
      window.localStorage.removeItem(key);
    } catch (error) {
        console.error('useLocalStorage.remove failed to remove error', error);
    }
  };

  return { storedValue, setValue, remove };
};
